import { generateObject, LanguageModel } from 'ai';
import dedent from 'dedent';
import { z } from 'zod';

export const GENERATE_PANEL_DESCRIPTION_PROMPT_VERSION = 3;

export type QueryType = 'promql' | 'logs';

export interface GeneratePanelNameAndDescriptionInput {
  /** Array of query strings from the panel */
  queries: string[];
  /** Type of each query (must match length of queries array) */
  queryTypes: QueryType[];
  /** Optional panel visualization type (e.g., "TimeSeriesChart", "StatChart") */
  panelType?: string;
  /** Current panel name to evaluate (if provided, AI will return empty string if adequate) */
  currentName?: string;
  /** Current panel description to evaluate (if provided, AI will return empty string if adequate) */
  currentDescription?: string;
}

export interface GeneratePanelNameAndDescriptionOutput {
  /** A short, descriptive panel name (max ~50 chars, no newlines) */
  name: string;
  /** A concise description explaining what the panel displays (1-2 sentences, no newlines) */
  description: string;
}

/** Schema for the generated output */
const outputSchema = z.object({
  name: z
    .string()
    .describe(
      'A short, descriptive panel name (maximum 50 characters). Should be concise and describe what the panel shows. Return empty string if the current name is adequate.'
    ),
  description: z
    .string()
    .describe(
      'A concise description (1-2 sentences, maximum 200 characters). Explains what the panel displays in plain language. Return empty string if the current description is adequate.'
    ),
});

/**
 * Removes newlines from a string, replacing them with spaces.
 */
function removeNewlines(text: string): string {
  return text.replace(/[\r\n]+/g, ' ').trim();
}

/**
 * Generates a human-readable name and description for a dashboard panel based on its queries.
 *
 * @param model - The language model to use for generation
 * @param input - The input containing queries and context
 * @param options - Optional settings; pass `abortSignal` (e.g. from TanStack Query's queryFn context) to cancel in-flight generation
 * @returns An object with name and description for the panel
 */
export async function generatePanelNameAndDescription(
  model: LanguageModel,
  input: GeneratePanelNameAndDescriptionInput,
  options?: { abortSignal?: AbortSignal }
): Promise<GeneratePanelNameAndDescriptionOutput> {
  const { queries, queryTypes, panelType, currentName, currentDescription } = input;

  if (queries.length === 0) {
    return { name: '', description: '' };
  }

  // Build query context for the prompt
  const queryContext = queries
    .map((query, index) => {
      const type = queryTypes[index] ?? 'promql';
      return `<query type="${type}">${query}</query>`;
    })
    .join('\n');

  const hasCurrentValues = Boolean(currentName?.trim() || currentDescription?.trim());

  const systemPrompt = dedent`
    You are an expert at explaining observability queries in plain language.
    
    Your task is to ${hasCurrentValues ? 'evaluate existing values and optionally suggest improvements for' : 'generate'} a panel name and description for a dashboard panel based on its queries.
    
    ## CRITICAL RULES - MUST FOLLOW
    1. Write content that a non-technical user can understand
    2. Focus on WHAT the panel shows, not HOW it works technically
    3. Do NOT include technical jargon like "PromQL", "rate()", or "sum by"
    4. Do NOT include the actual query syntax
    5. Do NOT include newlines in either the name or description
    6. Use active voice and present tense
    ${
      hasCurrentValues
        ? '7. Return an EMPTY STRING for name or description if the current value is adequate (accurate and descriptive)'
        : ''
    }
    
    ${
      hasCurrentValues
        ? `## EVALUATION RULES
    - If the current name accurately describes what the panel shows, return an empty string for name
    - If the current description accurately explains the panel's purpose, return an empty string for description
    - Only suggest a new value if the current one is:
      - Missing or empty
      - Inaccurate or misleading about what the queries measure
      - Too generic (e.g., "Panel 1", "New Panel", "Untitled")
      - Contains technical jargon that could be simplified
    - When in doubt, prefer keeping the existing value (return empty string)
    `
        : ''
    }
    ## NAME GUIDELINES
    - Maximum 50 characters
    - Should be a short, descriptive title
    - Capitalize appropriately (title case or sentence case)
    - Examples: "HTTP Error Rate", "Memory Usage by Instance", "Top API Endpoints"
    
    ## DESCRIPTION GUIDELINES
    - Maximum 200 characters
    - 1-2 sentences that explain what the panel displays
    - Should provide context beyond what the name conveys
    - Examples:
      - "Shows the percentage of HTTP requests that returned 5xx error codes."
      - "Displays memory consumption for the checkout service, broken down by instance."
    
    ## EXAMPLES
    
    Query: rate(http_requests_total{status=~"5.."}[$__rate_interval]) / rate(http_requests_total[$__rate_interval])
    Name: HTTP Error Rate
    Description: Percentage of HTTP requests that returned 5xx status codes.
    
    Query: histogram_quantile(0.99, sum by (le) (rate(request_duration_seconds_bucket[$__rate_interval])))
    Name: P99 Response Latency
    Description: 99th percentile response time, showing the latency experienced by the slowest 1% of requests.
    
    Query: sum by (instance) (container_memory_usage_bytes{app="checkout"})
    Name: Checkout Service Memory
    Description: Memory usage for the checkout service, broken down by instance.
    
    Query: topk(10, sum by (endpoint) (rate(api_calls_total[$__rate_interval])))
    Name: Top 10 API Endpoints
    Description: Most frequently called API endpoints ranked by request rate.
    
    Query: count(*) | groupBy(level)
    Type: logs
    Name: Log Level Distribution
    Description: Distribution of log entries by severity level.
  `;

  // Build current values context if provided
  const currentValuesContext = hasCurrentValues
    ? dedent`
      <current_values>
      ${currentName?.trim() ? `<current_name>${currentName.trim()}</current_name>` : '<current_name>(empty)</current_name>'}
      ${currentDescription?.trim() ? `<current_description>${currentDescription.trim()}</current_description>` : '<current_description>(empty)</current_description>'}
      </current_values>
      `
    : '';

  const inputPrompt = dedent`
    ${panelType ? `<panel_type>${panelType}</panel_type>` : ''}
    
    <queries>
    ${queryContext}
    </queries>
    ${currentValuesContext ? `\n${currentValuesContext}` : ''}
    ${hasCurrentValues ? 'Evaluate the current name and description. Return empty strings for values that are adequate, or suggest improvements for values that need updating.' : 'Generate a name and description for this dashboard panel.'}
  `;

  const result = await generateObject({
    model,
    schema: outputSchema,
    temperature: 0.1,
    maxOutputTokens: 350,
    system: systemPrompt,
    prompt: inputPrompt,
    abortSignal: options?.abortSignal,
    experimental_telemetry: {
      isEnabled: true,
    },
  });

  // Post-process to remove any newlines that may have been generated
  return {
    name: removeNewlines(result.object.name),
    description: removeNewlines(result.object.description),
  };
}

/** Minimal shape for a panel query so we can extract strings without depending on viz-framework/core. */
export interface PanelQueryForExtraction {
  spec?: { plugin?: { kind?: string; spec?: { query?: string } } };
}

/**
 * Extracts query strings and their types from panel queries for AI panel name/description generation.
 * Maps plugin kind to QueryType: LogsDataQuery -> 'logs', otherwise 'promql'.
 * Pure function; the caller should memoize (e.g. useMemo) when used in React.
 */
export function getPanelQueryStrings(queries: PanelQueryForExtraction[] | undefined): {
  queryStrings: string[];
  queryTypes: QueryType[];
} {
  const queryStrings: string[] = [];
  const queryTypes: QueryType[] = [];

  for (const query of queries ?? []) {
    const pluginSpec = query?.spec?.plugin;
    if (pluginSpec && typeof pluginSpec === 'object' && 'spec' in pluginSpec) {
      const spec = pluginSpec.spec as { query?: string } | undefined;
      if (spec?.query && typeof spec.query === 'string') {
        queryStrings.push(spec.query);
        queryTypes.push(pluginSpec.kind === 'LogsDataQuery' ? 'logs' : 'promql');
      }
    }
  }

  return { queryStrings, queryTypes };
}
